import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";
import { camelToSnake, snakeToCamel } from "caseparser";
import { Slot } from "./slots";

export const slotsApi = api
  .enhanceEndpoints({ addTagTypes: ["slots", "slotGroups"] })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      deleteSlot: builder.mutation({
        query: ({ branchId, id, all }) => {
          const params = new URLSearchParams();
          params.append("all", all);

          return {
            url: `branchs/${branchId}/slots/${id}?${params.toString()}`,
            method: RequestMethod.DELETE,
          };
        },
        invalidatesTags: ["slots", "slotGroups"],
      }),
      updateSlot: builder.mutation({
        query: ({
          id,
          branchId,
          userId,
          roomId,
          name,
          startDate,
          endDate,
          startTime,
          days,
          color,
          capacity,
          expectedAttendance,
          duration,
        }) => ({
          url: `branchs/${branchId}/slots/${id}`,
          method: RequestMethod.PUT,
          body: camelToSnake({
            id,
            name,
            startDate,
            endDate,
            startTime,
            days,
            color,
            roomId,
            userId,
            capacity,
            expectedAttendance,
            duration,
          }),
        }),
      }),
      createSlot: builder.mutation({
        query: ({
          branchId,
          userId,
          roomId,
          name,
          dateStart,
          dateEnd,
          startTime,
          days,
          color,
          capacity,
          expectedAttendance,
          duration,
        }) => ({
          url: `branchs/${branchId}/slots`,
          method: RequestMethod.POST,
          body: camelToSnake({
            name,
            dateStart,
            dateEnd,
            startTime,
            days,
            color,
            roomId,
            userId,
            capacity,
            expectedAttendance,
            duration,
          }),
        }),
        invalidatesTags: ["slots", "slotGroups"],
      }),
      createSlotNew: builder.mutation({
        query: ({
          branchId,
          date_end,
          date_start,
          slot_bond_group_ids,
          fee_ids,
          days,
          slot_place_id,
          duration,
          price,
          type_slot,
          start_time,
        }) => ({
          url: `/branchs/${branchId}/slots`,
          method: RequestMethod.POST,
          body: {
            date_end,
            date_start,
            slot_bond_group_ids,
            fee_ids,
            days,
            slot_place_id,
            duration,
            price,
            type_slot,
            start_time,
          },
        }),
      }),
      updateSlotNew: builder.mutation({
        query: ({
          slotId,
          branchId,
          date_end,
          date_start,
          slot_bond_group_ids,
          fee_ids,
          days,
          day,
          slot_place_id,
          duration,
          price,
          type_slot,
          start_time,
          all,
        }) => ({
          url: `/branchs/${branchId}/slots/${slotId}`,
          method: RequestMethod.PUT,
          body: {
            date_end,
            date_start,
            slot_bond_group_ids,
            fee_ids,
            days,
            day,
            slot_place_id,
            duration,
            price,
            type_slot,
            time_start: start_time,
            all,
          },
        }),
      }),
      slotTimes: builder.mutation({
        query: ({ branchId, cancellation, anticipation, previus }) => ({
          url: `branchs/${branchId}/slots/times`,
          method: RequestMethod.POST,
          body: {
            cancellation,
            anticipation,
            previus,
          },
        }),
      }),
      slots: builder.query({
        query: ({ branchId, groups, date_start, date_end }) => {
          const queryParams = new URLSearchParams();
          if (date_start) {
            queryParams.append("date_start", date_start);
          }
          if (date_end) {
            queryParams.append("date_end", date_end);
          }

          return {
            url: `branchs/${branchId}/slots/events?${queryParams.toString()}`,
            method: RequestMethod.POST,
            body: {
              groups,
            },
          };
        },
        transformResponse: (response: { data: any[] }) =>
          response.data.map<Slot>((slot: any) =>
            snakeToCamel({
              ...slot,
              start_date: slot.start_date,
              end_date: slot.end_date,
              start_time: slot.time_start,
              end_time: slot.time_end,
              fees: slot.fees?.split(","),
            })
          ),
        providesTags: ["slots"],
      }),
      slotGroups: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/slots/groups`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
        providesTags: ["slotGroups"],
      }),
      slotsUser: builder.query({
        query: ({ branchId, groups, userId }) => ({
          url: `branchs/${branchId}/slots/list`,
          method: RequestMethod.POST,
          body: {
            user_id: userId,
            groups,
          },
        }),
        transformResponse: (response: { data: any[] }) =>
          response.data.map<Slot>((slot: any) =>
            snakeToCamel({
              ...slot,
              start_date: slot.start_date,
              end_date: slot.end_date,
              start_time: slot.time_start,
              end_time: slot.time_end,
              fees: slot.fees?.split(","),
            })
          ),
        providesTags: ["slots"],
      }),
      slotsPlaces: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/slots/places`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
        providesTags: ["slotGroups"],
      }),
      bondGroups: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/slots/bond-groups`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
        providesTags: ["slotGroups"],
      }),

      createSlotGroup: builder.mutation({
        query: ({
          branchId,
          userId,
          roomId,
          name,
          dateStart,
          dateEnd,
          startTime,
          days,
          color,
          capacity,
          expectedAttendance,
          duration,
        }) => ({
          url: `branchs/${branchId}/slots/storeGroup`,
          method: RequestMethod.POST,
          body: camelToSnake({
            name,
            dateStart,
            dateEnd,
            startTime,
            days,
            color,
            roomId,
            userId,
            capacity,
            expectedAttendance,
            duration,
          }),
        }),
        invalidatesTags: ["slots", "slotGroups"],
      }),
      updateSlotGroup: builder.mutation({
        query: ({ branchId, name, description, color, places }) => ({
          url: `branchs/${branchId}/slots/storeGroup`,
          method: RequestMethod.POST,
          body: camelToSnake({
            name,
            description,
            color,
            places,
          }),
        }),
        invalidatesTags: ["slots", "slotGroups"],
      }),

      getListGroups: builder.query({
        query: ({ branchId }) => ({
          url: `/branchs/${branchId}/slots/groups`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
      }),
      createGroup: builder.mutation({
        query: ({ branchId, name, description, color, places }) => ({
          url: `/branchs/${branchId}/slots/storeGroup`,
          method: RequestMethod.POST,
          body: { name, description, color, places },
        }),
      }),
      updateGroup: builder.mutation({
        query: ({
          branchId,
          name,
          description,
          color,
          places,
          slot_group_id,
        }) => ({
          url: `/branchs/${branchId}/slots/updateGroup`,
          method: RequestMethod.POST,
          body: { name, description, color, places, slot_group_id },
        }),
      }),
      deleteGroup: builder.mutation({
        query: ({ branchId, id }) => ({
          url: `/branchs/${branchId}/slots/delete-group/${id}`,
          method: RequestMethod.DELETE,
        }),
      }),

      getSlotsBondGroups: builder.query({
        query: ({ branchId }) => ({
          url: `/branchs/${branchId}/slot-bonds`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
      }),
      deactivateSlotBond: builder.mutation({
        query: ({ branchId, bondId }: any) => {
          console.log("api bondId", branchId, bondId);

          return {
            url: `/branchs/${branchId}/slot-bonds/${bondId}/disable`,
            method: RequestMethod.PUT,
          };
        },
      }),
      enableSlotBond: builder.mutation({
        query: ({ branchId, bondId }: any) => {
          console.log("api bondId", branchId, bondId);

          return {
            url: `/branchs/${branchId}/slot-bonds/${bondId}/enable`,
            method: RequestMethod.PUT,
          };
        },
      }),
      createSlotBond: builder.mutation({
        query: ({
          branchId,
          bondId,
          name,
          description,
          price,
          type_slot,
          currency,
          branch_slot_bond,
          session,
          days,
          repeated_bonus,
          max_session,
          nameGroupBond,
          groupbond_id,
        }: any) => {
          return {
            url: `/branchs/${branchId}/slot-bonds`,
            method: RequestMethod.POST,
            body: {
              branch_id: branchId,
              name,
              description,
              price,
              type_slot,
              currency,
              branch_slot_bond,
              session,
              days,
              repeated_bonus,
              max_session,
              nameGroupBond,
              groupbond_id,
            },
          };
        },
      }),
      updateSlotBond: builder.mutation({
        query: ({
          branchId,
          bondId,
          name,
          description,
          price,
          type_slot,
          currency,
          branch_slot_bond,
          session,
          days,
          repeated_bonus,
          max_session,
          nameGroupBond,
          groupbond_id,
        }: any) => {
          return {
            url: `/branchs/${branchId}/slot-bonds/${bondId}`,
            method: RequestMethod.PUT,
            body: {
              branch_id: branchId,
              name,
              description,
              price,
              type_slot,
              currency,
              branch_slot_bond,
              session,
              days,
              repeated_bonus,
              max_session,
              nameGroupBond,
              groupbond_id,
            },
          };
        },
      }),
      deleteSlotBondGroup: builder.mutation({
        query: ({ branchId, bondId }: any) => ({
          url: `/branchs/${branchId}/slot-bonds/${bondId}`,
          method: RequestMethod.DELETE,
        }),
      }),
    }),
  });

export const {
  useSlotsQuery,
  useSlotGroupsQuery,
  useSlotTimesMutation,
  useDeleteSlotMutation,
  useUpdateSlotMutation,
  useCreateSlotMutation,
  useSlotsUserQuery,
  useSlotsPlacesQuery,
  useBondGroupsQuery,
  useGetListGroupsQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useDeleteGroupMutation,

  useCreateSlotNewMutation, // create slot new
  useUpdateSlotNewMutation, // update slot new
  useGetSlotsBondGroupsQuery, // get slots bond groups
  useDeactivateSlotBondMutation, // activate deactivate bond
  useEnableSlotBondMutation, // activate deactivate bond
  useCreateSlotBondMutation, // create slot bond
  useUpdateSlotBondMutation, // update slot bond
  useDeleteSlotBondGroupMutation, // delete slot bond group
} = slotsApi;
